import React, { useState, useRef } from "react";
import {
  Typography,
  Container,
  Grid,
  Paper,
  Popper,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { CorreosButton } from "../CorreosButton/CorreosButton";
import { CommonStyles } from "../../commons/CommonStyles";
import {
  DeleteVerificationUserEmail,
  DeleteVerificationUserPhone,
  DeleteVerificationDataAddress,
  DeleteVerificationDataDocument,
} from "../../services/ReasonService";
import {
  getErrorMessage,
  useOutsideAlerter,
} from "../../commons/Utils";
import { useTranslation } from "react-i18next";
import { AttributeCodes } from "../../commons/Attributes";
import { CorreosIcon } from "../CommonComponents/CorreosIcon/CorreosIcon.js";
import { iconNames } from "../../commons/EnumsGeneral.js";

export function DialogDelete(props) {
  const { verificable, tipo, finishOperation, getVerificableName, deleteVerificable, userEmails } = props;
  const commonClasses = CommonStyles();
  const wrapperRef = useRef(null);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [circularProgress, setCircularProgress] = useState(false);

  const handleCancel = () => {
    setAnchorEl(null);
    setOpenDelete(false);
  };

  const handleConfirm = () => {
    // Obtener el nombre verificable y la llamada correspondiente
    const auxName = getVerificableName(verificable, tipo);
    const { auxCall, auxText } = getDeleteAction(tipo, auxName, verificable);

    // Validar si hay al menos un correo electrónico verificado
    if (tipo === AttributeCodes.Email && isLastVerifiedEmail(userEmails)) {
      finishOperation("warning", t("verificationsDeleteLastEmail"));
      handleCancel();
      return;
    }
    // Mostrar progreso
    setCircularProgress(true);
    // Ejecutar la llamada y manejar la respuesta
    auxCall()
      .then((response) => {
        if (response && !response.Status) {
          finishOperation("success", auxText);
          deleteVerificable(verificable, tipo);
        } else {
          finishOperation("error", getErrorMessage(response, t));
        }
      })
      .catch((error) => {
        finishOperation("error", t("verificationsDeleteError"));
      })
      .finally(() => {
        handleCancel();
        setCircularProgress(false);
      });
  };

  // Función auxiliar para obtener la acción de eliminación
  const getDeleteAction = (tipo, auxName, verificable) => {
    switch (tipo) {
      case AttributeCodes.Email:
        return {
          auxCall: () => DeleteVerificationUserEmail(auxName),
          auxText: t("verificationsDeleteEmail"),
        };
      case AttributeCodes.Phone:
        return {
          auxCall: () => DeleteVerificationUserPhone(auxName),
          auxText: t("verificationsDeletePhone"),
        };
      case AttributeCodes.Alias:
        return {
          auxCall: () => DeleteVerificationDataAddress(verificable.id),
          auxText: t("verificationsDeleteAddress"),
        };
      case AttributeCodes.DocNumber:
        return {
          auxCall: () => DeleteVerificationDataDocument(verificable.idTypeUserDocument, auxName),
          auxText: t("verificationsDeleteDocument"),
        };
      default:
        return { auxCall: () => Promise.reject(), auxText: "" };
    }
  };

  // Función auxiliar para validar si es el último correo verificado
  const isLastVerifiedEmail = (userEmails) => {
    const verifiedEmails = userEmails.filter((email) => email.idLevel >= 2);
    return verifiedEmails.length <= 1;
  };

  const handleDelete = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenDelete(true);
  };

  useOutsideAlerter(wrapperRef, handleCancel);

  return (
    <Grid style={{ textAlign: "right" }}>
      <Tooltip title={t("delete")}>
        <IconButton
          className={commonClasses.iconButton}
          aria-label="menu"
          onClick={(event) => handleDelete(event)}
        >
          <CorreosIcon iconSelected={iconNames.delete} iconName="Delete Icon" iconState="enabled" iconScale={1.75} />
        </IconButton>
      </Tooltip>
      <Popper
        open={openDelete}
        anchorEl={anchorEl}
        placement={"top"}
        style={{ zIndex: 100 }}
        ref={wrapperRef}
      >
        <Paper>
          <Container>
            <Grid container style={{ paddingTop: "1em", paddingBottom: "1em" }}>
              <Grid item xs={12}>
                <Typography className={commonClasses.popperContentText}>
                  {t("verificatinosDeleteElement")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <CorreosButton
                  size="small"
                  onClick={() => handleCancel()}
                  className={commonClasses.leftButton}
                >
                  {t("no")}
                </CorreosButton>
              </Grid>
              <Grid item xs={6}>
                <CorreosButton
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleConfirm()}
                  className={commonClasses.rightButton}
                  circularProgress={circularProgress}
                >
                  {t("yes")}
                </CorreosButton>
              </Grid>
            </Grid>
          </Container>
        </Paper>
      </Popper>
    </Grid>
  );
}
